import React from 'react'
import { useRouteMatch } from 'react-router'

// Utils
import { isDraftsRoute, isProjectRoute, useFlag } from '@sketch/modules-common'
import {
  addCollectionToProject,
  createOptimisticCollection,
  removeSharesFromProject,
} from 'modules/collections/operations/utils'

// Hooks
import {
  DragAndDroppedShareIds,
  useShareListItemDragDrop,
} from 'modules/shares/hooks/useShareDragAndDrop'

// Components
import { Tooltip } from '@sketch/components'

// Styles
import { DragWrapper } from './ShareListItemDrag.styles'

// Types
import { useApolloClient } from '@sketch/gql-types'

interface ShareListItemDragProps {
  disabled?: boolean
  shareId: string
  projectId?: string
}

const ShareListItemDrag: React.FC<ShareListItemDragProps> = props => {
  const {
    children,
    disabled = false,
    shareId,
    projectId: projectIdentifier,
  } = props

  // TODO: Remove collections FF
  // https://github.com/sketch-hq/Cloud/issues/15999
  const isCollectionsFFEnabled = useFlag('collections')

  const cache = useApolloClient()
  const { path } = useRouteMatch()

  const isProjectView = isProjectRoute(path)
  const isDraftView = isDraftsRoute(path)

  const isValidViewToCreateCollection = isProjectView || isDraftView

  const handleDroppedShare = ({
    draggedShareId,
    droppedShareId,
  }: DragAndDroppedShareIds) => {
    // we need a valid project id to create a collection by drag and drop
    if (!projectIdentifier) {
      return
    }

    if (draggedShareId === droppedShareId) {
      // If share was dropped on itself, ignore
      return
    }

    if (!isValidViewToCreateCollection) {
      return
    }

    const optimisticCollection = createOptimisticCollection({
      cache,
      shareIds: [draggedShareId, droppedShareId],
    })

    addCollectionToProject({
      cache,
      projectIdentifier,
      collection: {
        __typename: 'Collection',
        ...optimisticCollection,
      },
    })

    removeSharesFromProject(cache, projectIdentifier, [
      { identifier: draggedShareId },
      { identifier: droppedShareId },
    ])
  }

  const {
    isDraggedOver,
    ...dragDropEvents
  } = useShareListItemDragDrop<HTMLDivElement>({
    disabled,
    shareId,
    onDropped: handleDroppedShare,
  })

  const canCreateCollectionByDragNDrop =
    isCollectionsFFEnabled && isValidViewToCreateCollection && !!isDraggedOver

  return (
    <DragWrapper
      data-testid="drag-wrapper"
      $isDraggedOver={canCreateCollectionByDragNDrop}
      {...dragDropEvents}
    >
      <Tooltip
        content="Create a new collection"
        visible={canCreateCollectionByDragNDrop}
        placement="top"
      >
        {children}
      </Tooltip>
    </DragWrapper>
  )
}

export default ShareListItemDrag
